import {
  createContext,
  useContext,
  useEffect,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';
import type { ContextualNavSection, ContextualBackLink } from '@vnetwork/ui/layout';

/**
 * A scope-specific sidebar a nested route registers with the app shell. When set,
 * {@link AppLayout} renders these `sections` (plus the `back` escape hatch) in the
 * main `ContextualSidebar` instead of the primary nav — Cloudflare-style scope
 * switching. Cleared automatically when the registering route unmounts.
 */
export interface ContextualNavState {
  sections: ContextualNavSection[];
  /** id of the active leaf (matches a `ContextualNavLeaf.id`). */
  activeId?: string;
  /** "Back to {parent}" link shown above the nav. */
  back?: ContextualBackLink;
  /** Short name of the console (`WAAP`): the last part of every page title inside it, before the brand. */
  title?: string;
  /** Optional block shown in the sidebar header (e.g. service identity), under "Back to …". */
  header?: ReactNode;
}

interface NavContextValue {
  scoped: ContextualNavState | null;
  setScoped: (s: ContextualNavState | null) => void;
}

const NavContext = createContext<NavContextValue | null>(null);

/**
 * Holds the currently-registered scoped sidebar. @vnetwork/ui's `ContextualSidebar`
 * is intentionally stateless, so the app owns the scope state here and feeds it in.
 */
export function NavProvider({ children }: { children: ReactNode }) {
  const [scoped, setScoped] = useState<ContextualNavState | null>(null);
  return <NavContext.Provider value={{ scoped, setScoped }}>{children}</NavContext.Provider>;
}

/** Read the registered scoped sidebar (null = use primary nav). */
export function useContextualNav(): ContextualNavState | null {
  return useContext(NavContext)?.scoped ?? null;
}

/**
 * Register a scope-specific sidebar from a nested route. `build` is re-run (and the
 * sidebar updated) whenever `deps` change — pass the route path + service id +
 * anything else the sections depend on (e.g. zone feature flags). The scoped nav is
 * cleared when the component unmounts so the shell falls back to the primary nav.
 */
export function useSetContextualNav(
  build: () => ContextualNavState | null,
  deps: DependencyList,
) {
  const setScoped = useContext(NavContext)?.setScoped;
  useEffect(() => {
    setScoped?.(build());
    return () => setScoped?.(null);
    // `build` is intentionally excluded — `deps` drive recomputation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
